import React from 'react';

import { Box, Button, radioClasses, Typography } from '@mui/material';

import { Field, Form, FormSpy } from '@hrlogics/react-components/form';

import {
  DateFormatPatterns,
  EMPLOYERS_DEFAULT_PIN_SETTINGS_FORM_VALUES,
} from '@clearverify/constants';
import { formatDate } from '@clearverify/utils';

import { EmployersTableListDocument, SignUpMethod, useEmployerUpdateMutation } from 'src/api';
import { RadioGroupField, SwitchField, TextField } from 'src/components/finalForm';
import { SIGN_UP_METHOD_OPTIONS } from 'src/constants/employer';
import { useNotification } from 'src/hooks/app';
import { useEmployerById, useEmployerParamId } from 'src/hooks/employer';
import { useCurrentUserPermissions } from 'src/hooks/user';
import type { SxProp } from 'src/types/mui';

import { EmployeeSettingsFormFields, EmployeeSettingsFormValues } from './types';
import { employeeSettingsFormValidationSchema } from './utils';

export const EmployerSettingsForm: React.FC = () => {
  const { isPlatformAdmin } = useCurrentUserPermissions();
  const employerId = useEmployerParamId();
  const { employer } = useEmployerById(employerId);

  const { createApolloContext } = useNotification();
  const [employerUpdateMutation] = useEmployerUpdateMutation({
    refetchQueries: [EmployersTableListDocument],
    context: createApolloContext({ successMessage: 'Employer successfully updated' }),
  });

  const onSubmit = React.useCallback(
    async (formData: EmployeeSettingsFormValues) => {
      if (!employer?.id) return;
      try {
        await employerUpdateMutation({
          variables: {
            where: { id: employer.id },
            data: {
              signUpMethod: formData.signUpMethod,
              setLiveAt:
                employer.setLiveAt && formData.isLive
                  ? undefined
                  : formData.isLive
                    ? new Date().toISOString()
                    : null,
              pinHint:
                formData.signUpMethod === SignUpMethod.Pin ? (formData.pinHint ?? null) : null,
              ...EMPLOYERS_DEFAULT_PIN_SETTINGS_FORM_VALUES,
            },
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [employer, employerUpdateMutation],
  );

  const initialValues = React.useMemo(
    (): Partial<EmployeeSettingsFormValues> => ({
      [EmployeeSettingsFormFields.signUpMethod]: employer?.signUpMethod,
      [EmployeeSettingsFormFields.pinHint]: employer?.pinHint,
      [EmployeeSettingsFormFields.isLive]: !!employer?.setLiveAt,
    }),
    [employer],
  );

  const setLiveAt = employer?.setLiveAt;

  return (
    <Form<EmployeeSettingsFormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      subscription={{ dirty: true, submitting: true, valid: true }}
      validationSchema={employeeSettingsFormValidationSchema}
    >
      {({ form, dirty, submitting, valid }) => (
        <Box sx={boxStyles} rowGap={3}>
          {isPlatformAdmin && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={settingsStyles}
            >
              <Box>
                <Typography variant="subtitle1" fontSize={16}>
                  Is Live{' '}
                  {setLiveAt
                    ? `(set at ${formatDate(setLiveAt, DateFormatPatterns.shortDateWithSlash)})`
                    : null}
                </Typography>
                <Typography variant="body1" color="text.secondary" mt={1}>
                  Activate this toggle when you are ready for verifications
                </Typography>
              </Box>
              <Field
                sx={switchFieldStyles}
                component={SwitchField}
                disabled={submitting}
                name={EmployeeSettingsFormFields.isLive}
              />
            </Box>
          )}

          <Box sx={settingsStyles}>
            <Typography variant="subtitle1" fontSize={16}>
              Employee Sign Up PIN
            </Typography>
            <Typography variant="body1" color="text.secondary" mt={1}>
              Employees will use this 8 digit PIN as the first part of the sign-up process to
              confirm their identity. <br /> This is an optional additional layer of security.
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Format:
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={titleDataSchemaStyles}>
              XXXX-YYYY where XXXX - 4 last digits of SSN, YYYY - year of birth
            </Typography>

            <Field
              fullWidth
              name={EmployeeSettingsFormFields.signUpMethod}
              component={RadioGroupField}
              options={SIGN_UP_METHOD_OPTIONS}
              disabled={submitting}
              sx={radioFieldStyles}
            />

            <FormSpy<EmployeeSettingsFormValues> subscription={{ values: true }}>
              {({ values }) => {
                if (values.signUpMethod === SignUpMethod.Pin) {
                  return (
                    <Box>
                      <Typography variant="body1" color="text.secondary" mt={1} mb={2}>
                        Enter your PIN hint for employees here (optional, 200 characters max)
                      </Typography>

                      <Field
                        name={EmployeeSettingsFormFields.pinHint}
                        label="PIN Hint"
                        component={TextField}
                        disabled={submitting}
                        fullWidth
                      />
                    </Box>
                  );
                }
                return null;
              }}
            </FormSpy>
          </Box>

          <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
            <Button
              color="error"
              disabled={!dirty}
              size="medium"
              type="reset"
              variant="outlined"
              onClick={() => form.restart()}
            >
              Reset
            </Button>
            <Button
              disabled={!dirty || !valid}
              loading={submitting}
              variant="contained"
              type="submit"
            >
              Save changes
            </Button>
          </Box>
        </Box>
      )}
    </Form>
  );
};

const radioFieldStyles: SxProp = {
  marginLeft: 1,
  [`& .${radioClasses.checked}`]: {
    borderColor: 'red',
  },
  [`& .${radioClasses.root}`]: {
    fontSize: '1rem',
  },
};

const boxStyles: SxProp = {
  display: 'flex',
  flexDirection: 'column',
};

const switchFieldStyles: SxProp = {
  marginRight: '-16px',
};

const settingsStyles: SxProp = theme => ({
  border: theme.constantStyles?.paperBorder,
  borderRadius: theme.constantStyles?.paperBorderRadius,
  padding: '20px 24px',
});

const titleDataSchemaStyles: SxProp = {
  borderBottom: '1px solid #D9D9D9',
  paddingBottom: '16px',
  marginBottom: '16px',
};
